import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Phone, Mail, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { LoadingSpinner } from '../common/Badges';
import { useLang } from '../../i18n';

const StatusBadge = ({ delivered, status }) => {
  const bad = ['undelivered', 'failed', 'bounced', 'suppressed', 'busy', 'no-answer', 'canceled'].includes(status);
  const cls = delivered ? 'bg-green-500/15 text-green-400'
    : bad ? 'bg-red-500/15 text-red-400' : 'bg-yellow-500/15 text-yellow-400';
  const Icon = delivered ? CheckCircle : bad ? XCircle : Clock;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${cls}`}>
      <Icon className="w-3 h-3" />{(status || 'pending').toUpperCase()}
    </span>
  );
};

const fmtDate = (d) => {
  try { return new Date(d).toLocaleString(); } catch { return d || ''; }
};

const Section = ({ icon: Icon, title, count, children, testId }) => (
  <div className="card p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-5 h-5 text-red-500" />
      <h2 className="text-lg font-bold text-white">{title}</h2>
      <span className="text-xs text-gray-500 font-bold">({count})</span>
    </div>
    <div className="overflow-x-auto">{children}</div>
  </div>
);

const AdminDeliveryStatus = () => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try { setData(await api('/api/admin/delivery-status')); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
  }, [load]);

  if (loading && !data) return <LoadingSpinner />;
  const sms = data?.sms || [];
  const calls = data?.calls || [];
  const emails = data?.emails || [];

  return (
    <div data-testid="admin-delivery-status" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-red-500 font-bold tracking-[0.3em] mb-1">LIVE</p>
          <h1 className="font-heading text-3xl font-bold text-white">{t('admin_delivery')}</h1>
          <p className="text-sm text-gray-500 mt-1">Auto-refreshes every 30s</p>
        </div>
        <button onClick={() => load()} className="flex items-center gap-2 px-4 py-2.5 border border-gray-700 rounded-lg text-gray-300 text-sm font-bold hover:border-red-500" data-testid="delivery-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <Section icon={Mail} title="Emails (Resend)" count={emails.length} testId="delivery-emails">
        <table className="w-full text-xs">
          <thead><tr className="text-[10px] text-gray-500 uppercase text-left">
            <th className="pb-2 pr-3">To</th><th className="pb-2 pr-3">Subject</th><th className="pb-2 pr-3">Date</th><th className="pb-2">Status</th></tr></thead>
          <tbody className="divide-y divide-gray-800/60">
            {emails.map((e, i) => (
              <tr key={i}>
                <td className="py-2 pr-3 text-gray-300">{Array.isArray(e.to) ? e.to.join(', ') : e.to}</td>
                <td className="py-2 pr-3 text-gray-400 max-w-[280px] truncate">{e.subject}</td>
                <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">{fmtDate(e.date)}</td>
                <td className="py-2"><StatusBadge delivered={e.delivered} status={e.status} /></td>
              </tr>
            ))}
            {emails.length === 0 && <tr><td colSpan="4" className="py-4 text-gray-500">No emails yet</td></tr>}
          </tbody>
        </table>
      </Section>

      <Section icon={MessageSquare} title="SMS (Twilio)" count={sms.length} testId="delivery-sms">
        <table className="w-full text-xs">
          <thead><tr className="text-[10px] text-gray-500 uppercase text-left">
            <th className="pb-2 pr-3">To</th><th className="pb-2 pr-3">Message</th><th className="pb-2 pr-3">Date</th><th className="pb-2">Status</th></tr></thead>
          <tbody className="divide-y divide-gray-800/60">
            {sms.map((m, i) => (
              <tr key={i}>
                <td className="py-2 pr-3 text-gray-300 whitespace-nowrap">{m.to}</td>
                <td className="py-2 pr-3 text-gray-400 max-w-[300px] truncate">{m.body}</td>
                <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">{fmtDate(m.date)}</td>
                <td className="py-2">
                  <StatusBadge delivered={m.delivered} status={m.status} />
                  {m.hint && <p className="text-[10px] text-red-400 mt-1">{m.error_code}: {m.hint}</p>}
                </td>
              </tr>
            ))}
            {sms.length === 0 && <tr><td colSpan="4" className="py-4 text-gray-500">No SMS yet</td></tr>}
          </tbody>
        </table>
      </Section>

      <Section icon={Phone} title="Voice Calls (Twilio)" count={calls.length} testId="delivery-calls">
        <table className="w-full text-xs">
          <thead><tr className="text-[10px] text-gray-500 uppercase text-left">
            <th className="pb-2 pr-3">To</th><th className="pb-2 pr-3">Date</th><th className="pb-2 pr-3">Duration</th><th className="pb-2">Status</th></tr></thead>
          <tbody className="divide-y divide-gray-800/60">
            {calls.map((c, i) => (
              <tr key={i}>
                <td className="py-2 pr-3 text-gray-300 whitespace-nowrap">{c.to}</td>
                <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">{fmtDate(c.date)}</td>
                <td className="py-2 pr-3 text-gray-400">{c.duration ? `${c.duration}s` : '—'}</td>
                <td className="py-2"><StatusBadge delivered={c.delivered} status={c.status} /></td>
              </tr>
            ))}
            {calls.length === 0 && <tr><td colSpan="4" className="py-4 text-gray-500">No calls yet</td></tr>}
          </tbody>
        </table>
      </Section>
    </div>
  );
};

export default AdminDeliveryStatus;
